import GetSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/get";
import PostSimpatraServiceProxy from "@/proxies/back-end/simpatra-service/post";

// Constants for API endpoints
const API_ENDPOINTS = {
  PURCHASE_FULFILLMENT: (purchaseId, vendorId) =>
    `orders/v1/purchase/${encodeURIComponent(purchaseId)}/vendor/${encodeURIComponent(vendorId)}/invoice`,
  PURCHASE_INVOICE: purchaseId => `orders/v1/purchase/${encodeURIComponent(purchaseId)}/invoice`,
  FAX_INVOICE: purchaseId => `orders/v1/purchase/${encodeURIComponent(purchaseId)}/invoice/fax`,
  RE_ORDER: (locationId, purchaseGeneratedId) =>
    `orders/v1/buyer/${encodeURIComponent(locationId)}/purchase/${encodeURIComponent(purchaseGeneratedId)}/re-order`,
};

// Reusable proxy instances
const getProxy = new GetSimpatraServiceProxy();
const postProxy = new PostSimpatraServiceProxy();

/**
 * Validates required parameters
 * @param {Object} params - Parameters to validate
 * @param {Array} required - Array of required parameter names
 * @throws {Error} If required parameters are missing
 */
const validateParams = (params, required) => {
  const missing = required.filter(param => !params[param]);
  if (missing.length > 0) {
    throw new Error(`Missing required parameters: ${missing.join(", ")}`);
  }
};

/**
 * Handles API response consistently
 * @param {Object} response - API response
 * @returns {Object|null} Response data or null
 */
const handleResponse = response => {
  return response?.data || null;
};

/**
 * Fetches purchase fulfillment data by vendor ID
 * @param {Object} context - Vuex context (unused)
 * @param {Object} params - Action parameters
 * @param {string} params.purchaseId - Purchase ID
 * @param {string} params.vendorId - Vendor ID
 * @param {string} [params.type] - Optional type filter
 * @returns {Promise<Object|null>} Purchase fulfillment data
 */
const fetchPurchaseFulfillmentByVendorId = async (_, { purchaseId, vendorId, type }) => {
  try {
    validateParams({ purchaseId, vendorId }, ["purchaseId", "vendorId"]);

    const queryParams = type ? { type } : {};
    const query = API_ENDPOINTS.PURCHASE_FULFILLMENT(purchaseId, vendorId);
    const response = await getProxy.execute(query, queryParams);

    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching purchase fulfillment:", error);
    throw error;
  }
};

/**
 * Fetches purchase data
 * @param {Object} context - Vuex context (unused)
 * @param {string} purchaseId - Purchase ID
 * @returns {Promise<Object|null>} Purchase data
 */
const fetchPurchase = async (_, purchaseId) => {
  try {
    validateParams({ purchaseId }, ["purchaseId"]);

    const query = API_ENDPOINTS.PURCHASE_INVOICE(purchaseId);
    const response = await getProxy.execute(query);

    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching purchase:", error);
    throw error;
  }
};

/**
 * Sends invoice via fax
 * @param {Object} context - Vuex context (unused)
 * @param {Object} params - Action parameters
 * @param {Object} params.payload - Fax payload data
 * @param {string} params.purchaseId - Purchase ID
 * @returns {Promise<Object>} API response
 */
const faxInvoice = async (_, { payload, purchaseId }) => {
  try {
    validateParams({ payload, purchaseId }, ["payload", "purchaseId"]);

    const query = API_ENDPOINTS.FAX_INVOICE(purchaseId);
    const response = await postProxy.execute(query, payload, {
      notificationParams: {
        displaySuccessMessage: true,
        successNotificationMsg: "Order has been processed successfully!",
      },
    });

    return response;
  } catch (error) {
    console.error("Error faxing invoice:", error);
    throw error;
  }
};

/**
 * Re-orders line items for a purchase
 * @param {Object} context - Vuex context (unused)
 * @param {Object} params - Action parameters
 * @param {Object} params.payload - Re-order payload data
 * @param {string} params.locationId - Location ID
 * @param {string} params.purchaseGeneratedId - Purchase generated ID
 * @returns {Promise<Object>} API response
 */
const reOrderLineItems = async (_, { locationId, generatedId }) => {
  try {
    validateParams({ locationId, generatedId }, ["locationId", "generatedId"]);

    const query = API_ENDPOINTS.RE_ORDER(locationId, generatedId);
    const response = await postProxy.execute(query, null, {
      notificationParams: {
        displaySuccessMessage: true,
        successNotificationMsg: "Re-order has been processed successfully!",
      },
    });

    return response;
  } catch (error) {
    console.error("Error processing re-order:", error);
    throw error;
  }
};

export default {
  fetchPurchaseFulfillmentByVendorId,
  fetchPurchase,
  faxInvoice,
  reOrderLineItems,
};
